// What CTH Admin's apps share through the hub (Tony, 2026-10-02): a few settings (table row
// height, rows per page, column icons, the quick panels' keys), the Scratchpad text and the
// CLAUDE.md editor. Inside CTH Admin they live on the hub's server (http://localhost:47600:
// /api/shared, /api/scratchpad, /api/claude-md) and a change is passed to the other apps through
// the hub ({ cthHub: 'shared-changed' } and friends). Everywhere else (the website, a bare
// window) the same values live in this browser's localStorage, so everything still works.
// CLAUDE.md has no fallback: the hub is the only way to the file.
import * as cloud from './cloud.js';

export const SHARED_DEFAULTS = { rowHeight: 36, openMode: 'side', rowsPerPage: 100, columnIcons: false, scratchKey: 'mod+i', claudeKey: 'mod+o' };
const SHARED_KEY = 'cthvw.shared.v1';
const SCRATCH_KEY = 'cthvw.scratch.v1';
// The hub's address: the page that frames us. Tests of the Mac app point it at a mock
// (window.__CTH_HUB_TEST__, the Mac app only), never at the real hub.
export const hubBase = (() => {
  if (!cloud.local) return null;
  try { if (cloud.inHub) return location.ancestorOrigins?.[0] || 'http://localhost:47600'; } catch (_) { return 'http://localhost:47600'; }
  return globalThis.__CTH_HUB_TEST__ || null;
})();
export const inAdmin = !!hubBase;

async function hub(path, method = 'GET', body) {
  const r = await fetch(hubBase + path, { method, cache: 'no-store', headers: { 'X-CTH-Local': '1', ...(body ? { 'Content-Type': 'application/json' } : {}) }, body: body ? JSON.stringify(body) : undefined });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) { const e = new Error(j.error || `The hub answered ${r.status}`); e.status = r.status; e.body = j; throw e; }
  return j;
}
/** Tell the hub (it passes it on to the other apps). */
export function toHub(msg) {
  if (!hubBase) return;
  try { window.parent.postMessage(msg, cloud.inHub ? hubBase : location.origin); } catch (_) { /* fine */ }
}

// ---- shared settings -------------------------------------------------------------------
const readLocal = (key) => { try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch (_) { return null; } };
const writeLocal = (key, v) => { try { localStorage.setItem(key, JSON.stringify(v)); } catch (_) { /* private window */ } };
const clean = (s) => {
  const o = { ...SHARED_DEFAULTS, ...(s && typeof s === 'object' ? s : {}) };
  o.rowHeight = Math.max(28, Math.min(96, Math.round(Number(o.rowHeight) || 36)));
  o.rowsPerPage = Number(o.rowsPerPage) > 0 ? Math.round(Number(o.rowsPerPage)) : 100;
  o.columnIcons = !!o.columnIcons;
  for (const k of ['scratchKey', 'claudeKey']) if (typeof o[k] !== 'string' || !o[k]) o[k] = SHARED_DEFAULTS[k];
  return o;
};
export const shared = clean(readLocal(SHARED_KEY));
const watchers = new Set();
/** fn(shared) runs after every change (here, in another app, in another tab). */
export const onShared = (fn) => { watchers.add(fn); };
const emit = () => { for (const fn of watchers) { try { fn(shared); } catch (e) { console.error(e); } } };
const take = (s) => { const next = clean(s); const changed = JSON.stringify(next) !== JSON.stringify(shared); for (const k of Object.keys(shared)) delete shared[k]; Object.assign(shared, next); writeLocal(SHARED_KEY, shared); return changed; };
/** Read the settings again (at start, and when the hub says another app changed them). */
export async function loadShared() {
  if (hubBase) { try { const { settings } = await hub('/api/shared'); if (take(settings)) emit(); } catch (_) { /* the hub is restarting: the last copy stands */ } }
  else if (take(readLocal(SHARED_KEY))) emit();
  return shared;
}
/** Change settings: { rowHeight: 36 }. Saved to the hub (or this browser) and passed on. */
export async function setShared(patch) {
  take({ ...shared, ...patch }); emit();
  if (!hubBase) return shared;
  try { const { settings } = await hub('/api/shared', 'PUT', { settings: patch }); take(settings); toHub({ cthHub: 'shared-changed' }); } catch (_) { /* kept in this browser; sent with the next change */ }
  return shared;
}
if (!hubBase) addEventListener('storage', (e) => { if (e.key === SHARED_KEY && take(readLocal(SHARED_KEY))) emit(); });

// ---- the two quick panels' text ------------------------------------------------------------
/** The Scratchpad: { text, updated }. */
export const scratchApi = {
  async read() { if (hubBase) return hub('/api/scratchpad'); const d = readLocal(SCRATCH_KEY); return { text: String(d?.text || ''), updated: Number(d?.updated) || 0 }; },
  async write(text) {
    if (hubBase) { const r = await hub('/api/scratchpad', 'PUT', { text }); toHub({ cthHub: 'scratch-changed' }); return r; }
    const updated = Date.now(); writeLocal(SCRATCH_KEY, { text, updated }); return { ok: true, updated };
  },
  /** Another tab of the website changed it (outside CTH Admin). */
  watch(fn) { if (!hubBase) addEventListener('storage', (e) => { if (e.key === SCRATCH_KEY) fn(); }); },
};
/** Tony's global CLAUDE.md, through the hub only: { text, updated, path }; a save needs the time it was loaded. */
export const claudeApi = hubBase ? {
  read: () => hub('/api/claude-md'),
  async write(text, base) { const r = await hub('/api/claude-md', 'PUT', { text, base }); toHub({ cthHub: 'claude-changed' }); return r; },
} : null;

// ---- keys ("mod+i", "mod+shift+k": the hub reads the same form) ------------------------------
export function keyMatches(e, combo) {
  const parts = String(combo || '').toLowerCase().split('+'); const k = parts[parts.length - 1];
  if (!k) return false;
  return String(e.key).toLowerCase() === k && (e.metaKey || e.ctrlKey) === parts.includes('mod') && e.shiftKey === parts.includes('shift') && e.altKey === parts.includes('alt');
}
/** "mod+shift+k" from a key press, or null while only modifiers are down (or it has none: a bare letter would steal typing). */
export function comboOf(e) {
  const k = String(e.key || '');
  if (['Meta', 'Control', 'Shift', 'Alt', 'CapsLock', 'Dead', 'Process'].includes(k)) return null;
  const fn = /^F\d{1,2}$/.test(k);
  if (!(e.metaKey || e.ctrlKey || e.altKey) && !fn) return null;
  return [e.metaKey || e.ctrlKey ? 'mod' : '', e.altKey ? 'alt' : '', e.shiftKey ? 'shift' : '', k === ' ' ? 'space' : k.toLowerCase()].filter(Boolean).join('+');
}
const MAC = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');
/** "mod+shift+i" as "⇧⌘I" (Mac) or "Ctrl+Shift+I". */
export function keyLabel(combo) {
  const parts = String(combo || '').toLowerCase().split('+').filter(Boolean); const k = parts.pop() || '';
  const key = k === 'space' ? 'Space' : k.length === 1 ? k.toUpperCase() : k.charAt(0).toUpperCase() + k.slice(1);
  if (MAC) return `${parts.includes('alt') ? '⌥' : ''}${parts.includes('shift') ? '⇧' : ''}${parts.includes('mod') ? '⌘' : ''}${key}`;
  return [parts.includes('mod') ? 'Ctrl' : '', parts.includes('alt') ? 'Alt' : '', parts.includes('shift') ? 'Shift' : '', key].filter(Boolean).join('+');
}
